import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';

const BlogContent = ({ content }) => {
  // Memoize processed content to avoid re-processing on every render
  const processedContent = useMemo(() => {
    if (!content) return '';
    
    // Split by lines
    const lines = content.split('\n');
    const processedLines = [];
    
    for (let line of lines) {
      // Skip empty lines
      if (!line.trim()) {
        processedLines.push('');
        continue;
      }
      
      // H2 headings (##)
      if (line.startsWith('## ')) {
        processedLines.push(`<h2 class="text-2xl font-bold mt-8 mb-4">${line.replace('## ', '')}</h2>`);
      }
      // H3 headings (###)
      else if (line.startsWith('### ')) {
        processedLines.push(`<h3 class="text-xl font-semibold mt-6 mb-3">${line.replace('### ', '')}</h3>`);
      }
      // Bold text (**text**)
      else if (line.includes('**')) {
        const processed = line.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>');
        processedLines.push(`<p class="mb-4 leading-relaxed">${processed}</p>`);
      }
      // Bullet points (-)
      else if (line.trim().startsWith('- ')) {
        processedLines.push(`<li class="ml-6 mb-2">${line.replace(/^- /, '')}</li>`);
      }
      // Checkmarks (✅)
      else if (line.trim().startsWith('✅')) {
        processedLines.push(`<div class="flex items-start space-x-2 mb-2"><span class="text-green-600">✅</span><span>${line.replace('✅', '').trim()}</span></div>`);
      }
      // Regular paragraph
      else {
        processedLines.push(`<p class="mb-4 leading-relaxed">${line}</p>`);
      }
    }
    
    const htmlContent = processedLines.join('\n');
    
    // Sanitize HTML to prevent XSS attacks
    return DOMPurify.sanitize(htmlContent, {
      ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'a', 'ul', 'ol', 'li', 'strong', 'em', 'blockquote', 'code', 'pre', 'img', 'br', 'hr', 'div', 'span'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'id'],
      ALLOW_DATA_ATTR: false
    });
  }, [content]);
  
  return (
    <div 
      className="blog-content prose prose-lg max-w-none"
      dangerouslySetInnerHTML={{ __html: processedContent }}
    />
  );
};

export default BlogContent;
